import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  FACET_CONTEXTS,
  blankFacet,
  formsFromEntry,
  storedFacetsFromForms,
} from "../lib/facetForm";

export const RELATIONSHIP_CONTEXTS = FACET_CONTEXTS;

function normalizeContexts(contexts, fallback = "professional") {
  const requested = Array.isArray(contexts) ? contexts : [contexts];
  const normalized = RELATIONSHIP_CONTEXTS.filter((context) => requested.includes(context));
  return normalized.length ? normalized : [fallback];
}

export function contextsFromSelection(selection) {
  return selection === "both" ? RELATIONSHIP_CONTEXTS : normalizeContexts(selection);
}

export function selectionFromContexts(contexts) {
  const normalized = normalizeContexts(contexts);
  return normalized.length === RELATIONSHIP_CONTEXTS.length ? "both" : normalized[0];
}

function formFacets(entry, entries) {
  const stored = formsFromEntry(entry, entries);
  return {
    professional: stored.professional || blankFacet("professional"),
    personal: stored.personal || blankFacet("personal"),
  };
}

function entryContexts(entry, fallback) {
  const contexts = RELATIONSHIP_CONTEXTS.filter((context) => entry?.facets?.[context]);
  return contexts.length ? contexts : normalizeContexts(fallback);
}

export function createRelationshipDraftState({
  entry = null,
  entries = [],
  initialContexts = ["professional"],
  initialActiveContext = "professional",
  sourceKey = entry?.id || null,
} = {}) {
  const enabledContexts = entryContexts(entry, initialContexts);
  const activeContext = enabledContexts.includes(initialActiveContext)
    ? initialActiveContext
    : enabledContexts[0];

  return {
    sourceKey,
    facets: formFacets(entry, entries),
    enabledContexts,
    activeContext,
    removedContexts: [],
    pendingRemoval: null,
    dirty: false,
    submissionStatus: "idle",
    submissionError: "",
  };
}

function withFacetDefaults(facets, contexts) {
  const next = { ...facets };
  contexts.forEach((context) => {
    if (!next[context]) next[context] = blankFacet(context);
  });
  return next;
}

export function relationshipDraftReducer(state, action) {
  switch (action.type) {
    case "hydrate": {
      const sameSource = action.state.sourceKey === state.sourceKey;
      if (
        !action.force &&
        sameSource &&
        (
          state.dirty ||
          state.pendingRemoval ||
          state.submissionStatus === "submitting" ||
          state.submissionStatus === "failed"
        )
      ) {
        return state;
      }

      if (!action.preserveContexts) {
        if (!sameSource || !action.state.enabledContexts.includes(state.activeContext)) {
          return action.state;
        }
        return { ...action.state, activeContext: state.activeContext };
      }

      return {
        ...action.state,
        enabledContexts: state.enabledContexts,
        activeContext: state.enabledContexts.includes(state.activeContext)
          ? state.activeContext
          : state.enabledContexts[0],
        facets: withFacetDefaults(action.state.facets, state.enabledContexts),
      };
    }

    case "set-facets": {
      const nextFacets = typeof action.value === "function"
        ? action.value(state.facets)
        : action.value;
      if (!nextFacets || nextFacets === state.facets) return state;
      return {
        ...state,
        facets: nextFacets,
        dirty: true,
        submissionStatus: "idle",
        submissionError: "",
      };
    }

    case "select-contexts": {
      const enabledContexts = normalizeContexts(action.contexts);
      const activeContext = enabledContexts.includes(state.activeContext)
        ? state.activeContext
        : enabledContexts[0];
      return {
        ...state,
        facets: withFacetDefaults(state.facets, enabledContexts),
        enabledContexts,
        activeContext,
        removedContexts: state.removedContexts.filter(
          (context) => !enabledContexts.includes(context),
        ),
        pendingRemoval: null,
        dirty: true,
        submissionStatus: "idle",
        submissionError: "",
      };
    }

    case "set-active-context":
      if (!state.enabledContexts.includes(action.context)) return state;
      return { ...state, activeContext: action.context, pendingRemoval: null };

    case "add-context": {
      if (!RELATIONSHIP_CONTEXTS.includes(action.context)) return state;
      const enabledContexts = normalizeContexts([...state.enabledContexts, action.context]);
      return {
        ...state,
        facets: withFacetDefaults(state.facets, enabledContexts),
        enabledContexts,
        activeContext: action.context,
        removedContexts: state.removedContexts.filter((context) => context !== action.context),
        pendingRemoval: null,
        dirty: true,
        submissionStatus: "idle",
        submissionError: "",
      };
    }

    case "request-removal":
      if (!state.enabledContexts.includes(action.context) || state.enabledContexts.length < 2) {
        return state;
      }
      return { ...state, pendingRemoval: action.context };

    case "cancel-removal":
      return state.pendingRemoval ? { ...state, pendingRemoval: null } : state;

    case "confirm-removal": {
      const context = state.pendingRemoval;
      if (!context || state.enabledContexts.length < 2) return state;
      const enabledContexts = state.enabledContexts.filter((item) => item !== context);
      return {
        ...state,
        enabledContexts,
        activeContext: enabledContexts.includes(state.activeContext)
          ? state.activeContext
          : enabledContexts[0],
        removedContexts: [...new Set([...state.removedContexts, context])],
        pendingRemoval: null,
        dirty: true,
        submissionStatus: "idle",
        submissionError: "",
      };
    }

    case "submission-start":
      return { ...state, submissionStatus: "submitting", submissionError: "" };

    case "submission-success":
      return { ...state, dirty: false, submissionStatus: "succeeded", submissionError: "" };

    case "submission-failure":
      return { ...state, submissionStatus: "failed", submissionError: action.message };

    case "clear-error":
      return {
        ...state,
        submissionStatus: state.submissionStatus === "failed" ? "idle" : state.submissionStatus,
        submissionError: "",
      };

    default:
      return state;
  }
}

/**
 * Owns the complete add/edit draft lifecycle. Live snapshots may hydrate a
 * pristine draft, but cannot replace local edits for the same relationship.
 */
export function useRelationshipDraft(options = {}) {
  const {
    entry = null,
    entries = [],
    initialContexts = ["professional"],
    initialActiveContext = "professional",
    sourceKey = entry?.id || null,
    liveHydration = true,
  } = options;
  const initialOptions = useRef({ initialContexts, initialActiveContext });
  const [state, dispatch] = useReducer(
    relationshipDraftReducer,
    { entry, entries, initialContexts, initialActiveContext, sourceKey },
    createRelationshipDraftState,
  );
  const mounted = useRef(false);
  const submissionLock = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!liveHydration) return;
    dispatch({
      type: "hydrate",
      state: createRelationshipDraftState({
        entry,
        entries,
        initialContexts: initialOptions.current.initialContexts,
        initialActiveContext: initialOptions.current.initialActiveContext,
        sourceKey,
      }),
    });
  }, [entry, entries, liveHydration, sourceKey]);

  const hydrate = useCallback((nextEntry, hydrateOptions = {}) => {
    const nextEntries = hydrateOptions.entries || entries;
    const nextSourceKey = Object.prototype.hasOwnProperty.call(hydrateOptions, "sourceKey")
      ? hydrateOptions.sourceKey
      : nextEntry?.id || null;
    dispatch({
      type: "hydrate",
      force: Boolean(hydrateOptions.force),
      preserveContexts: Boolean(hydrateOptions.preserveContexts),
      state: createRelationshipDraftState({
        entry: nextEntry,
        entries: nextEntries,
        initialContexts: state.enabledContexts,
        initialActiveContext: state.activeContext,
        sourceKey: nextSourceKey,
      }),
    });
  }, [entries, state.activeContext, state.enabledContexts]);

  const setFacets = useCallback((value) => dispatch({ type: "set-facets", value }), []);
  const selectContexts = useCallback(
    (contexts) => dispatch({ type: "select-contexts", contexts }),
    [],
  );
  const selectContext = useCallback(
    (context) => dispatch({ type: "set-active-context", context }),
    [],
  );
  const addContext = useCallback(
    (context) => dispatch({ type: "add-context", context }),
    [],
  );
  const requestRemoval = useCallback(
    (context) => dispatch({ type: "request-removal", context }),
    [],
  );
  const cancelRemoval = useCallback(() => dispatch({ type: "cancel-removal" }), []);
  const confirmRemoval = useCallback(() => dispatch({ type: "confirm-removal" }), []);
  const clearError = useCallback(() => dispatch({ type: "clear-error" }), []);

  const storedFacets = useCallback((nextEntries = entries) => {
    const activeForms = Object.fromEntries(
      state.enabledContexts.map((context) => [context, state.facets[context] || blankFacet(context)]),
    );
    const stored = storedFacetsFromForms(activeForms, nextEntries);
    state.removedContexts.forEach((context) => { stored[context] = null; });
    return stored;
  }, [entries, state.enabledContexts, state.facets, state.removedContexts]);

  const submit = useCallback(async (mutation, submitOptions = {}) => {
    if (submissionLock.current) return false;
    submissionLock.current = true;
    dispatch({ type: "submission-start" });
    try {
      await mutation();
      if (mounted.current) dispatch({ type: "submission-success" });
      return true;
    } catch (error) {
      const fallback = "Changes could not be saved. Try again.";
      let message = submitOptions.errorMessage || fallback;
      if (typeof message === "function") message = message(error);
      if (mounted.current) dispatch({ type: "submission-failure", message: message || fallback });
      return false;
    } finally {
      submissionLock.current = false;
    }
  }, []);

  const missingContext = RELATIONSHIP_CONTEXTS.find(
    (context) => !state.enabledContexts.includes(context),
  ) || null;

  return useMemo(() => ({
    ...state,
    isDirty: state.dirty,
    isSubmitting: state.submissionStatus === "submitting",
    contextSelection: selectionFromContexts(state.enabledContexts),
    missingContext,
    hydrate,
    setFacets,
    selectContexts,
    selectContext,
    addContext,
    requestRemoval,
    cancelRemoval,
    confirmRemoval,
    clearError,
    storedFacets,
    submit,
  }), [
    state,
    missingContext,
    hydrate,
    setFacets,
    selectContexts,
    selectContext,
    addContext,
    requestRemoval,
    cancelRemoval,
    confirmRemoval,
    clearError,
    storedFacets,
    submit,
  ]);
}

function createNoteState(sourceKey, value) {
  const text = value || "";
  return {
    sourceKey,
    value: text,
    baseline: text,
    dirty: false,
    status: "idle",
    error: "",
  };
}

function noteDraftReducer(state, action) {
  switch (action.type) {
    case "hydrate":
      if (action.sourceKey === state.sourceKey) {
        if (state.dirty || state.status === "saving") return state;
        if ((action.value || "") === state.baseline) return state;
      }
      return createNoteState(action.sourceKey, action.value);
    case "edit":
      return {
        ...state,
        value: action.value,
        dirty: action.value !== state.baseline,
        status: "idle",
        error: "",
      };
    case "save-start":
      return { ...state, status: "saving", error: "" };
    case "save-success":
      if (state.sourceKey !== action.sourceKey) return state;
      if (state.value !== action.value) {
        return { ...state, baseline: action.value, dirty: true, status: "idle", error: "" };
      }
      return {
        ...state,
        baseline: action.value,
        dirty: false,
        status: "saved",
        error: "",
      };
    case "save-failure":
      if (state.sourceKey !== action.sourceKey) return state;
      return { ...state, dirty: true, status: "error", error: action.message };
    default:
      return state;
  }
}

/** Async lifecycle for the inline private note editor. */
export function useRelationshipNoteDraft({
  sourceKey,
  value,
  onSave,
  errorMessage = "The note could not be saved. Try again.",
}) {
  const [state, dispatch] = useReducer(noteDraftReducer, null, () => createNoteState(sourceKey, value));
  const latest = useRef(state);
  const savingSources = useRef(new Set());
  const mounted = useRef(false);
  latest.current = state;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    dispatch({ type: "hydrate", sourceKey, value });
  }, [sourceKey, value]);

  const setValue = useCallback((nextValue) => {
    dispatch({ type: "edit", value: nextValue });
  }, []);

  const save = useCallback(async () => {
    const draft = latest.current;
    if (!onSave || !draft.dirty || savingSources.current.has(draft.sourceKey)) {
      return !draft.dirty;
    }
    savingSources.current.add(draft.sourceKey);
    const submittedValue = draft.value;
    const submittedSourceKey = draft.sourceKey;
    dispatch({ type: "save-start" });
    try {
      await onSave(submittedValue);
      if (mounted.current) {
        dispatch({
          type: "save-success",
          sourceKey: submittedSourceKey,
          value: submittedValue,
        });
      }
      return true;
    } catch (_) {
      if (mounted.current) {
        dispatch({
          type: "save-failure",
          sourceKey: submittedSourceKey,
          message: errorMessage,
        });
      }
      return false;
    } finally {
      savingSources.current.delete(submittedSourceKey);
    }
  }, [errorMessage, onSave]);

  return {
    value: state.value,
    setValue,
    save,
    isDirty: state.dirty,
    status: state.status,
    error: state.error,
    isSaving: state.status === "saving",
  };
}
